import React, { FC } from "react";
import { createPortal } from "react-dom";
import { Draggable } from "react-beautiful-dnd";

import { classNames } from "../../../../../utils/common/styling";

import { AccordionDetails, IconButton, styled } from "@mui/material";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";

import RightIcon from "@mui/icons-material/ChevronRightRounded";
import EditIcon from "@mui/icons-material/CreateOutlined";
import DragIcon from "@mui/icons-material/DragIndicatorOutlined";

import SidebarCta from "../../SidebarCta/SidebarCta";

import { styles } from "./Collection.styles";
import { ICollection } from "./ICollection";
import SectionCTAsGroup from "../../SectionCTAsGroup/SectionCTAsGroup";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(() => ({
  padding: "0px 16px 0px 18px",
  minHeight: 0,
  "& .MuiAccordionSummary-content": {
    margin: 0,
    cursor: "default",
  },
}));

const Collection: FC<ICollection.IProps> = ({
  expanded,
  showActions,
  showDivider: showDiviver,
  index,
  collection,
  selectedChannelId,
  canDragDrop = true,
  onClick,
  onClickChannel,
  onEdit,
  onAddChannel,
  onChange,
}) => {
  const { label, channels, id } = collection;
  const dragPortal = document.body;

  return (
    <Draggable draggableId={`draggable-${id}`} index={index}>
      {(provided, snapshot) => {
        const collectionDragElement = (
          <div
            className={classNames(styles.root)}
            ref={provided.innerRef}
            {...provided.draggableProps}
          >
            <Accordion
              classes={{
                root: classNames(
                  styles.accordionRoot,
                  snapshot.isDragging && styles.draggable,
                ),
              }}
              expanded={expanded}
            >
              <AccordionSummary>
                <div
                  className={classNames(
                    styles.header,
                    !showDiviver && styles.noMargin,
                  )}
                >
                  <div
                    className={classNames(
                      styles.label,
                      !showActions && styles.labelWithoutActions,
                    )}
                    onClick={(event) =>
                      onClick?.({ collection: collection, event, index })
                    }
                  >
                    {label}
                    <IconButton
                      className={classNames(
                        styles.expandCta,
                        expanded && styles.expanded,
                      )}
                    >
                      <RightIcon />
                    </IconButton>
                  </div>

                  {showActions && (
                    <div className={styles.actions}>
                      <IconButton
                        onClick={(event) => onEdit?.({ collection, event })}
                      >
                        <EditIcon />
                      </IconButton>
                      <div {...provided.dragHandleProps}>
                        <DragIcon className={styles.dragCta} />
                      </div>
                    </div>
                  )}
                </div>
              </AccordionSummary>
              <AccordionDetails className={styles.details}>
                <SectionCTAsGroup
                  value={selectedChannelId}
                  type="collection"
                  sections={channels?.map((channel) => ({
                    id: channel.id,
                    icon: channel.icon,
                    label: channel.label,
                    sectionData: channel,
                  }))}
                  onClick={({ section, index, event }) =>
                    onClickChannel?.({
                      channel: section.sectionData,
                      index,
                      event,
                    })
                  }
                  onChange={onChange}
                  canDragDrop={canDragDrop}
                  showOnlyDrag
                />
                {showActions && (
                  <SidebarCta
                    label="Add channel"
                    ctaVariant="secondary"
                    className={styles.addCta}
                    onClick={(event) => onAddChannel?.({ collection, event })}
                  />
                )}
              </AccordionDetails>
            </Accordion>
            {showDiviver && !snapshot.isDragging && (
              <hr className={styles.divider} />
            )}
          </div>
        );

        return snapshot.isDragging
          ? createPortal(collectionDragElement, dragPortal)
          : collectionDragElement;
      }}
    </Draggable>
  );
};

export default Collection;
